import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Spec 228 — theme preference.
 *
 * `tokens.css` implements all three states on its own: `:root` is daylight,
 * `:root[data-theme="dark"]` is winter night, and an un-stamped root under
 * `prefers-color-scheme: dark` follows the operating system. This provider's
 * only job is to stamp or clear the attribute — it defines no colours.
 *
 * No toggle ships here. The control belongs in the bar, which spec 229 builds;
 * until then the stored preference and the OS setting are the inputs.
 */
export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'geo_observatory_theme';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (next: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(): Theme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // Private mode or blocked storage: fall through to the OS setting.
  }
  return 'system';
}

function apply(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStored);

  useEffect(() => {
    apply(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Preference is not persisted; the session still honours it.
    }
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
